import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  RefreshCw, 
  Search, 
  ChevronRight, 
  ChevronDown,
  Folder,
  FolderOpen,
  Tag,
  Download,
  AlertCircle
} from "lucide-react";

const API = "/api";

export default function CategoriesPage() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [connectionStatus, setConnectionStatus] = useState(null);

  useEffect(() => {
    checkConnection();
  }, [token]);

  const checkConnection = async () => {
    try {
      const response = await axios.get(`${API}/nebim/connection-status?token=${token}`);
      setConnectionStatus(response.data);
      if (response.data.connected) {
        fetchCategories();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Connection check error:", error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/nebim/hierarchy?token=${token}&lang_code=TR`);
      if (response.data.success && Array.isArray(response.data.data)) {
        setCategories(response.data.data);
        // Varsayılan olarak ana kategorileri aç
        const mainCategories = response.data.data
          .filter(c => !c.ProductHierarchyLevelCode.includes(">"))
          .map(c => c.ProductHierarchyLevelCode);
        setExpandedNodes(new Set(mainCategories));
        toast.success(`${response.data.data.length} kategori yüklendi`);
      } else {
        toast.error("Kategori verisi alınamadı");
      }
    } catch (error) {
      console.error("Fetch categories error:", error);
      toast.error(error.response?.data?.detail || "Kategoriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  // Hiyerarşik yapıyı oluştur
  const hierarchicalData = useMemo(() => {
    if (!categories.length) return [];

    // Ana kategoriler (> içermeyen)
    const mainCategories = categories.filter(c => !c.ProductHierarchyLevelCode.includes(">"));
    
    // Alt kategorileri grupla
    const getChildren = (parentCode) => {
      return categories.filter(c => {
        const parts = c.ProductHierarchyLevelCode.split(" > ");
        if (parts.length < 2) return false;
        const immediateParent = parts.slice(0, -1).join(" > ");
        return immediateParent === parentCode;
      });
    };

    const buildTree = (items, level = 0) => {
      return items.map(item => {
        const children = getChildren(item.ProductHierarchyLevelCode);
        return {
          ...item,
          level,
          children: children.length > 0 ? buildTree(children, level + 1) : []
        };
      });
    };

    return buildTree(mainCategories);
  }, [categories]);

  // Filtrelenmiş kategoriler
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return hierarchicalData;
    
    const searchLower = searchTerm.toLowerCase();
    
    const filterTree = (items) => {
      return items.reduce((acc, item) => {
        const matchesSelf = item.ProductHierarchyLevel.toLowerCase().includes(searchLower);
        const filteredChildren = filterTree(item.children || []);
        
        if (matchesSelf || filteredChildren.length > 0) {
          acc.push({
            ...item,
            children: filteredChildren
          });
        }
        return acc;
      }, []);
    };
    
    return filterTree(hierarchicalData);
  }, [hierarchicalData, searchTerm]);

  const toggleNode = (code) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(code)) {
        newSet.delete(code);
      } else {
        newSet.add(code);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allCodes = categories.map(c => c.ProductHierarchyLevelCode);
    setExpandedNodes(new Set(allCodes));
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Kategori öğesi render
  const renderCategoryItem = (item, depth = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedNodes.has(item.ProductHierarchyLevelCode);
    const levelParts = item.ProductHierarchyLevelCode.split(" > ");
    const levelCode = levelParts[levelParts.length - 1];

    return (
      <div key={item.ProductHierarchyLevelCode}>
        <div 
          className={`
            flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer
            hover:bg-slate-100 transition-colors
            ${depth === 0 ? 'bg-slate-50' : ''}
          `}
          style={{ marginLeft: `${depth * 20}px` }}
          onClick={() => hasChildren && toggleNode(item.ProductHierarchyLevelCode)}
          data-testid={`category-${levelCode}`}
        >
          {hasChildren ? (
            <button className="p-0.5 hover:bg-slate-200 rounded" data-testid={`toggle-${levelCode}`}>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}
          
          {hasChildren ? (
            isExpanded ? (
              <FolderOpen className="w-5 h-5 text-amber-500" />
            ) : (
              <Folder className="w-5 h-5 text-amber-500" />
            )
          ) : (
            <Tag className="w-4 h-4 text-slate-400" />
          )}
          
          <span className={`flex-1 ${depth === 0 ? 'font-medium' : 'text-sm'}`}>
            {item.ProductHierarchyLevel.split(" > ").pop()}
          </span>
          
          <Badge variant="outline" className="text-xs">
            {levelCode}
          </Badge>
          
          {hasChildren && (
            <Badge variant="secondary" className="text-xs">
              {item.children.length}
            </Badge>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="category-children">
            {item.children.map(child => renderCategoryItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!connectionStatus?.connected) {
    return (
      <div className="space-y-6 fade-in" data-testid="categories-no-connection">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kategori Yönetimi</h1>
          <p className="text-slate-500 mt-1">Nebim kategori hiyerarşisini görüntüleyin</p>
        </div>
        
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-amber-800">Nebim Bağlantısı Gerekli</h3>
                <p className="text-amber-700 mt-1 text-sm">
                  Kategorileri görüntülemek için önce Nebim V3 API bağlantınızı yapılandırmanız gerekiyor.
                </p>
                <Button 
                  className="mt-4" 
                  onClick={() => window.location.href = "/settings"}
                  data-testid="btn-go-to-settings"
                >
                  Bağlantı Ayarlarına Git
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="categories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Kategori Yönetimi</h1>
          <p className="text-slate-500 mt-1">
            Nebim kategori hiyerarşisi - Toplam {categories.length} kategori
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={collapseAll}
            data-testid="btn-collapse-all"
          >
            Hepsini Kapat
          </Button>
          <Button 
            variant="outline" 
            onClick={expandAll}
            data-testid="btn-expand-all"
          >
            Hepsini Aç
          </Button>
          <Button 
            onClick={fetchCategories} 
            disabled={loading}
            data-testid="btn-refresh-categories"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Yenile
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Kategori ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="input-search-category"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64" data-testid="loading-categories">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="no-categories">
              {searchTerm ? "Arama sonucu bulunamadı" : "Henüz kategori yok"}
            </div>
          ) : (
            <ScrollArea className="h-[500px] pr-4">
              <div className="space-y-1">
                {filteredCategories.map(item => renderCategoryItem(item))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* İstatistikler */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">
              {categories.filter(c => !c.ProductHierarchyLevelCode.includes(">")).length}
            </div>
            <div className="text-sm text-slate-500">Ana Kategori</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">
              {categories.filter(c => c.ProductHierarchyLevelCode.includes(">")).length}
            </div>
            <div className="text-sm text-slate-500">Alt Kategori</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">
              {Math.max(...categories.map(c => c.ProductHierarchyLevelCode.split(" > ").length))}
            </div>
            <div className="text-sm text-slate-500">Maksimum Derinlik</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">
              {expandedNodes.size}
            </div>
            <div className="text-sm text-slate-500">Açık Düğüm</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
